// Réglages d'impression pour une estampe multiple (miroir du DTO API
// MultipleprintPrintSettings). Listes de champs par modèle + groupes d'options
// (onglet « Options d'affichage », parité avec le drawer œuvre).

export type MultiplePrintTemplate = "simple" | "complete" | "binder";

const identityFields = [
  "showArtist",
  "showTitle",
  "showRegistrationNumber",
  "showInventoryNumber",
  "showSubtitle",
  "showOtherTitle",
  "showCreationDate",
  "showFormerNumber",
  "showOtherNumber",
] as const;

const baseInfoFields = [
  "showTechnique",
  "showDimensions",
  "hideDimensionLabel",
  "showImageDimensions",
  "showType",
  "showEditor",
  "showPrinter",
  "showStatus",
] as const;

const textFields = [
  "showSummary",
  "showLongDescription",
  "showPublicNotes",
] as const;

export const simpleTemplateFields = [
  ...identityFields,
  ...baseInfoFields,
  "showCopyNumber",
  ...textFields,
  "showSecondaryImages",
  "hideDate",
] as const;

const relationsFields = [
  "oeuvresDansExposition",
  "oeuvresDansVentes",
  "oeuvresDansBibliographie",
] as const;

export const completeTemplateFields = [
  ...identityFields,
  ...baseInfoFields,
  "showCopyNumber",
  ...textFields,
  ...relationsFields,
  "showExemplaires",
  "showSecondaryImages",
  "hideDate",
] as const;

export const binderTemplateFields = [
  "showRegistrationNumber",
  "showCreationDate",
  "showSubtitle",
  "showOtherTitle",
  "showTechnique",
  "showDimensions",
  "showImageDimensions",
  "showCopyNumber",
  "showSignature",
  "showInscriptionsBack",
  "showType",
  "showEditor",
  "showPrinter",
  "showStatus",
  ...textFields,
  "showExemplaires",
  "hideDate",
] as const;

export type MultiplePrintSettingKey =
  | (typeof simpleTemplateFields)[number]
  | (typeof completeTemplateFields)[number]
  | (typeof binderTemplateFields)[number];

/** Champs d'un exemplaire proposés au sélecteur (retour client). */
export const exemplaireFieldKeys = [
  "registrationNumber",
  "number",
  "inventoryNumber",
  "editionNumber",
  "type",
  "printer",
  "editor",
  "technique",
  "support",
  "signature",
  "dimensions",
  "status",
  "localisation",
  "notes",
] as const;

export type ExemplaireFieldKey = (typeof exemplaireFieldKeys)[number];

export type MultiplePrintSettings = Partial<
  Record<MultiplePrintSettingKey, boolean>
> & {
  exemplaireFields?: Partial<Record<ExemplaireFieldKey, boolean>>;
};

/** Tous les champs d'un modèle (utilisé pour les défauts). */
export function getFieldsForTemplate(
  template: MultiplePrintTemplate,
): readonly MultiplePrintSettingKey[] {
  if (template === "binder") return binderTemplateFields;
  if (template === "complete") return completeTemplateFields;
  return simpleTemplateFields;
}

// ===========================================================================
// Groupes d'options (onglet « Options d'affichage », 2 colonnes)
// ===========================================================================

export type MpSettingsGroup = {
  key: string;
  titleKey: string;
  column: "left" | "right";
  fields: readonly string[];
};

/**
 * Un seul jeu de groupes, filtré par modèle via {@link getGroupFields} : un
 * groupe ne montre que les champs réellement présents dans le modèle courant,
 * et disparaît s'il se retrouve vide.
 */
export const MP_SETTINGS_GROUPS: Record<string, MpSettingsGroup> = {
  infosDeBase: {
    key: "infosDeBase",
    titleKey: "mpPrintGroupInfosDeBase",
    column: "left",
    fields: [
      "showArtist",
      "showTitle",
      "showRegistrationNumber",
      "showCreationDate",
      "showTechnique",
      "showDimensions",
      "showImageDimensions",
      "hideDimensionLabel",
      "showSignature",
      "showInscriptionsBack",
    ],
  },
  description: {
    key: "description",
    titleKey: "mpPrintGroupDescription",
    column: "left",
    fields: [
      "showSubtitle",
      "showOtherTitle",
      "showInventoryNumber",
      "showCopyNumber",
      "showFormerNumber",
      "showOtherNumber",
    ],
  },
  production: {
    key: "production",
    titleKey: "mpPrintGroupProduction",
    column: "left",
    fields: ["showType", "showEditor", "showPrinter", "showStatus"],
  },
  textes: {
    key: "textes",
    titleKey: "mpPrintGroupTextes",
    column: "left",
    fields: ["showSummary", "showLongDescription", "showPublicNotes"],
  },
  exemplaires: {
    key: "exemplaires",
    titleKey: "mpPrintGroupExemplaires",
    column: "right",
    fields: ["showExemplaires"],
  },
  autres: {
    key: "autres",
    titleKey: "mpPrintGroupAutres",
    column: "right",
    fields: ["hideDate"],
  },
  // Colonne « enregistrements liés » : son propre onglet dédié, pas la grille
  // d'options — mais le groupe sert à dériver les champs (getGroupFields).
  relations: {
    key: "relations",
    titleKey: "mpPrintGroupRelations",
    column: "right",
    fields: [
      "oeuvresDansExposition",
      "oeuvresDansVentes",
      "oeuvresDansBibliographie",
    ],
  },
};

export function getSettingsGroups(): Record<string, MpSettingsGroup> {
  return MP_SETTINGS_GROUPS;
}

/** Champs d'un groupe réellement présents dans le modèle courant. */
export function getGroupFields(
  template: MultiplePrintTemplate,
  groupKey: string,
): readonly string[] {
  const group = MP_SETTINGS_GROUPS[groupKey];
  if (!group) return [];
  const templateFields = getFieldsForTemplate(template) as readonly string[];
  return group.fields.filter((f) => templateFields.includes(f));
}

/** Tous les réglages connus initialisés à `false`. */
export function getAllSettingsFields(): MultiplePrintSettings {
  const all = [
    ...simpleTemplateFields,
    ...completeTemplateFields,
    ...binderTemplateFields,
  ] as readonly string[];
  const settings: MultiplePrintSettings = {};
  for (const f of all) {
    settings[f as MultiplePrintSettingKey] = false;
  }
  return settings;
}

/** Libellés FR (fallback) — clé i18n `bo.print.mp.<key>`. */
export const MULTIPLEPRINT_SETTING_LABELS: Record<string, string> = {
  showArtist: "Artiste / auteur",
  showTitle: "Titre",
  showRegistrationNumber: "N° d'enregistrement",
  showInventoryNumber: "N° d'inventaire",
  showSubtitle: "Sous-titre",
  showOtherTitle: "Autre titre",
  showCreationDate: "Date de création",
  showFormerNumber: "Ancien numéro",
  showOtherNumber: "Autre numéro",
  showTechnique: "Technique / support",
  showDimensions: "Dimensions",
  hideDimensionLabel: 'Masquer le libellé « Dimensions »',
  showImageDimensions: "Dim. image",
  showCopyNumber: "Copie / ex. n°",
  showSignature: "Signature",
  showInscriptionsBack: "Inscriptions au dos",
  showType: "Type",
  showEditor: "Éditeur",
  showPrinter: "Fabricant / Imprimeur",
  showStatus: "Statut",
  showSummary: "Résumé",
  showLongDescription: "Épreuves - Tirage",
  showPublicNotes: "Notes publiques",
  showSecondaryImages: "Images secondaires",
  showExemplaires: "Lister les exemplaires",
  hideDate: "Masquer la date d'impression",
  oeuvresDansExposition: "Expositions",
  oeuvresDansVentes: "Ventes publiques",
  oeuvresDansBibliographie: "Bibliographies",
};

/** Libellés FR des champs d'exemplaire — clé i18n `bo.print.mp.ex.<key>`. */
export const EXEMPLAIRE_FIELD_LABELS: Record<ExemplaireFieldKey, string> = {
  registrationNumber: "N° d'enregistrement",
  number: "Copie / ex. n°",
  inventoryNumber: "N° d'inventaire",
  editionNumber: "N° d'édition",
  type: "Type",
  printer: "Imprimeur",
  editor: "Éditeur",
  technique: "Technique",
  support: "Support",
  signature: "Signature",
  dimensions: "Dimensions",
  status: "Statut",
  localisation: "Localisation",
  notes: "Notes",
};

/**
 * Réglages masqués sur la fiche d'un exemplaire (données du multiple parent
 * non pertinentes — épreuves / tirage).
 */
export const EXEMPLAIRE_HIDDEN_SETTING_KEYS = [
  "showLongDescription",
] as const;

/** Réglage propre à la fiche exemplaire (pas au multiple parent). */
export const EXEMPLAIRE_ONLY_SETTING_KEYS = ["showCopyNumber"] as const;
